/**
 * Stimulus exemplars for the Stimulus Perception Check.
 *
 * Keyed by "{communicationStyle}_{groupFocus}" (6 condition cells).
 * Each set contains 4 stimuli:
 *   - 2 standalone posts (one pro, one contra)
 *   - 2 post+comment pairs (rate the comment only)
 *
 * All post/comment content uses {{topic}} as a placeholder, which is
 * interpolated at runtime with the actual topic name (e.g., "AI Exam
 * Monitoring"). This keeps stimuli connected to the discussion the
 * participant just experienced.
 *
 * The content MUST embody the condition's characteristics:
 *   - emotional: short, visceral, morally certain, no hedging
 *   - rational: evidence-oriented, qualified, deliberative
 *   - neutral: issue-focused, no group references
 *   - ingroup: solidarity, "we", shared identity
 *   - outgroup: adversarial, names opponents, attributes motives
 */

const STIMULUS_EXEMPLARS = {
  // =========================================================================
  // EMOTIONAL × NEUTRAL (collapsed complexity, issue-focused)
  // =========================================================================
  emotional_neutral: {
    label: "Emotional × Neutral",
    stimuli: [
      {
        stimulusId: "en_post_pro",
        type: "post",
        post: {
          author: { username: "truthseeker_88", displayName: "Jordan M." },
          content: "{{topic}} is EXACTLY what we need to get right. People are being affected and we just sit here pretending everything is fine? No. This should have been addressed years ago and I'm tired of waiting.",
          timestamp: "3h",
          likes: 18,
          comments: 5,
          reposts: 2,
        },
      },
      {
        stimulusId: "en_post_con",
        type: "post",
        post: {
          author: { username: "cleareyesopen", displayName: "Sam K." },
          content: "Wake up. The current approach to {{topic}} will make things WORSE not better. The people pushing this have NO idea what the real consequences are. I can't believe we're even having this conversation.",
          timestamp: "2h",
          likes: 14,
          comments: 7,
          reposts: 1,
        },
      },
      {
        stimulusId: "en_pair_pro",
        type: "post_comment",
        post: {
          author: { username: "concerned_citizen", displayName: "Alex R." },
          content: "I really don't understand why anyone would oppose progress on {{topic}}. The evidence is clear and the need is obvious.",
          timestamp: "4h",
          likes: 9,
          comments: 3,
          reposts: 0,
        },
        comment: {
          author: { username: "voiceoftruth22", displayName: "Casey L." },
          content: "Exactly!! The evidence on {{topic}} IS clear and people who ignore it are just burying their heads in the sand. This matters TOO much to keep debating \ud83d\udcaf",
          timestamp: "3h",
          likes: 6,
        },
      },
      {
        stimulusId: "en_pair_con",
        type: "post_comment",
        post: {
          author: { username: "openminded_01", displayName: "Riley T." },
          content: "I think the proposed changes to {{topic}} could genuinely help a lot of people. The benefits seem to outweigh the risks.",
          timestamp: "5h",
          likes: 11,
          comments: 4,
          reposts: 1,
        },
        comment: {
          author: { username: "realtalknow", displayName: "Morgan P." },
          content: "Benefits outweigh the risks?? Tell that to people who'll actually be AFFECTED by {{topic}}. Easy to say when you're not the one paying the price \ud83d\ude24",
          timestamp: "4h",
          likes: 8,
        },
      },
    ],
  },

  // =========================================================================
  // EMOTIONAL × INGROUP (collapsed complexity, solidarity framing)
  // =========================================================================
  emotional_ingroup: {
    label: "Emotional \u00d7 Ingroup",
    stimuli: [
      {
        stimulusId: "ei_post_pro",
        type: "post",
        post: {
          author: { username: "standtogether_", displayName: "Taylor W." },
          content: "We KNOW what's right when it comes to {{topic}}. People like us have been saying this for years and nobody listened. Now the evidence is undeniable. We need to stand together on this \ud83d\ude4c",
          timestamp: "3h",
          likes: 22,
          comments: 6,
          reposts: 3,
        },
      },
      {
        stimulusId: "ei_post_con",
        type: "post",
        post: {
          author: { username: "protectourown", displayName: "Jamie H." },
          content: "The current direction on {{topic}} will hurt people like us the most. We've been through enough already. When are we going to start looking out for our own? This is NOT acceptable.",
          timestamp: "2h",
          likes: 17,
          comments: 8,
          reposts: 2,
        },
      },
      {
        stimulusId: "ei_pair_pro",
        type: "post_comment",
        post: {
          author: { username: "hopeful_future", displayName: "Drew B." },
          content: "Looking at this from every angle, I think the approach to {{topic}} makes sense for most people.",
          timestamp: "4h",
          likes: 8,
          comments: 3,
          reposts: 0,
        },
        comment: {
          author: { username: "ourfight_2026", displayName: "Quinn S." },
          content: "It doesn't just make sense \u2014 it's what WE'VE been fighting for on {{topic}}. Our community has pushed for this and we can't stop now. We're in this together \u270a",
          timestamp: "3h",
          likes: 11,
        },
      },
      {
        stimulusId: "ei_pair_con",
        type: "post_comment",
        post: {
          author: { username: "pragmatist99", displayName: "Avery C." },
          content: "The supporters of changes to {{topic}} raise some valid points. I think we should consider both sides.",
          timestamp: "5h",
          likes: 6,
          comments: 5,
          reposts: 0,
        },
        comment: {
          author: { username: "notonyou", displayName: "Reese D." },
          content: "Both sides?? WE are the ones who'll suffer the consequences of {{topic}}. I'm tired of people who aren't affected telling us to be reasonable. We need to protect OUR people.",
          timestamp: "4h",
          likes: 9,
        },
      },
    ],
  },

  // =========================================================================
  // EMOTIONAL × OUTGROUP (collapsed complexity, adversarial framing)
  // =========================================================================
  emotional_outgroup: {
    label: "Emotional \u00d7 Outgroup",
    stimuli: [
      {
        stimulusId: "eo_post_pro",
        type: "post",
        post: {
          author: { username: "callthemout", displayName: "Blake N." },
          content: "The people blocking progress on {{topic}} know EXACTLY what they're doing. They don't care about anyone but themselves. Their selfishness is literally harming people and they sleep fine at night \ud83d\ude21",
          timestamp: "3h",
          likes: 19,
          comments: 9,
          reposts: 3,
        },
      },
      {
        stimulusId: "eo_post_con",
        type: "post",
        post: {
          author: { username: "seethrough_lies", displayName: "Dakota F." },
          content: "The supporters of {{topic}} are either naive or deliberately misleading people. They push this agenda knowing it'll fail, because THEY won't be the ones dealing with the fallout. Disgusting.",
          timestamp: "2h",
          likes: 15,
          comments: 7,
          reposts: 2,
        },
      },
      {
        stimulusId: "eo_pair_pro",
        type: "post_comment",
        post: {
          author: { username: "middleground", displayName: "Harper J." },
          content: "I can see arguments on both sides of {{topic}}. It's a complex issue with no easy answers.",
          timestamp: "4h",
          likes: 7,
          comments: 4,
          reposts: 0,
        },
        comment: {
          author: { username: "nomorelies_", displayName: "Rowan G." },
          content: "No easy answers?? The opponents of {{topic}} are LYING to you. They have financial interests in blocking this and they don't care who gets hurt. Stop falling for their garbage \ud83e\udd2e",
          timestamp: "3h",
          likes: 10,
        },
      },
      {
        stimulusId: "eo_pair_con",
        type: "post_comment",
        post: {
          author: { username: "justthefacts_", displayName: "Emery V." },
          content: "{{topic}} has strong institutional backing. Most experts seem to agree it's the right direction.",
          timestamp: "5h",
          likes: 10,
          comments: 5,
          reposts: 1,
        },
        comment: {
          author: { username: "exposethem", displayName: "Sage M." },
          content: "\"Experts\" who are paid by the same people pushing {{topic}}. These elitists don't live in the real world. They'll say anything to protect their position. WAKE UP.",
          timestamp: "4h",
          likes: 8,
        },
      },
    ],
  },

  // =========================================================================
  // RATIONAL × NEUTRAL (preserved complexity, issue-focused)
  // =========================================================================
  rational_neutral: {
    label: "Rational \u00d7 Neutral",
    stimuli: [
      {
        stimulusId: "rn_post_pro",
        type: "post",
        post: {
          author: { username: "evidencebased", displayName: "Chris A." },
          content: "The data from pilot programs on {{topic}} suggests net positive outcomes, though implementation challenges remain. On balance, the proposed framework addresses more risks than it creates. Worth examining the longitudinal studies.",
          timestamp: "3h",
          likes: 12,
          comments: 4,
          reposts: 2,
        },
      },
      {
        stimulusId: "rn_post_con",
        type: "post",
        post: {
          author: { username: "criticalreview", displayName: "Pat E." },
          content: "While the stated goals around {{topic}} are reasonable, the implementation plan has significant gaps. The cost-benefit analysis doesn't account for several indirect effects, and the timeline seems optimistic given comparable rollouts.",
          timestamp: "2h",
          likes: 10,
          comments: 6,
          reposts: 1,
        },
      },
      {
        stimulusId: "rn_pair_pro",
        type: "post_comment",
        post: {
          author: { username: "detailoriented", displayName: "Robin L." },
          content: "I'm not fully convinced the current approach to {{topic}} is right. There seem to be overlooked downsides.",
          timestamp: "4h",
          likes: 7,
          comments: 3,
          reposts: 0,
        },
        comment: {
          author: { username: "policywatch_", displayName: "Lane T." },
          content: "That's a fair concern. However, the comparative analysis on {{topic}} shows this approach outperforms alternatives on 3 of 4 key metrics. The downsides exist but are manageable with the proposed safeguards.",
          timestamp: "3h",
          likes: 5,
        },
      },
      {
        stimulusId: "rn_pair_con",
        type: "post_comment",
        post: {
          author: { username: "progressnow_", displayName: "Kai S." },
          content: "The research on {{topic}} clearly supports this direction. Most independent analyses reach similar conclusions.",
          timestamp: "5h",
          likes: 9,
          comments: 4,
          reposts: 1,
        },
        comment: {
          author: { username: "nuancematters", displayName: "Finley R." },
          content: "The research on {{topic}} is less conclusive than presented. Two recent meta-analyses found mixed results, and the effect sizes are modest. I'd want stronger evidence before supporting broad implementation.",
          timestamp: "4h",
          likes: 6,
        },
      },
    ],
  },

  // =========================================================================
  // RATIONAL × INGROUP (preserved complexity, solidarity framing)
  // =========================================================================
  rational_ingroup: {
    label: "Rational \u00d7 Ingroup",
    stimuli: [
      {
        stimulusId: "ri_post_pro",
        type: "post",
        post: {
          author: { username: "communitydata", displayName: "Hayden W." },
          content: "As someone from an affected community, I've followed the research on {{topic}} closely. The evidence supports this direction, and people like us \u2014 who understand the issue from lived experience \u2014 have a responsibility to advocate based on facts, not just feelings.",
          timestamp: "3h",
          likes: 15,
          comments: 5,
          reposts: 2,
        },
      },
      {
        stimulusId: "ri_post_con",
        type: "post",
        post: {
          author: { username: "ourperspective", displayName: "Skyler B." },
          content: "The people most affected by {{topic}} \u2014 our community \u2014 have consistently raised concerns backed by local data. We've seen similar approaches fail in our context before. Our collective experience deserves weight in this analysis.",
          timestamp: "2h",
          likes: 13,
          comments: 7,
          reposts: 1,
        },
      },
      {
        stimulusId: "ri_pair_pro",
        type: "post_comment",
        post: {
          author: { username: "neutralobserver", displayName: "Phoenix D." },
          content: "Interesting developments on {{topic}}. I wonder what the stakeholders most affected think about this.",
          timestamp: "4h",
          likes: 6,
          comments: 3,
          reposts: 0,
        },
        comment: {
          author: { username: "fromtheground", displayName: "Ellis J." },
          content: "I can speak to that \u2014 people in our situation have been tracking {{topic}}. The evidence aligns with our experience, and our advocacy groups have endorsed it. We bring both data and perspective to this debate.",
          timestamp: "3h",
          likes: 8,
        },
      },
      {
        stimulusId: "ri_pair_con",
        type: "post_comment",
        post: {
          author: { username: "policyforward", displayName: "Remy K." },
          content: "{{topic}} has broad institutional support. The consensus among analysts seems quite strong.",
          timestamp: "5h",
          likes: 8,
          comments: 4,
          reposts: 0,
        },
        comment: {
          author: { username: "weknowbetter", displayName: "Arden C." },
          content: "Institutional consensus doesn't capture our reality on {{topic}}. People like us have documented specific failures in similar implementations. Our community's research and experience should carry weight \u2014 we've done the work.",
          timestamp: "4h",
          likes: 7,
        },
      },
    ],
  },

  // =========================================================================
  // RATIONAL × OUTGROUP (preserved complexity, adversarial framing)
  // =========================================================================
  rational_outgroup: {
    label: "Rational \u00d7 Outgroup",
    stimuli: [
      {
        stimulusId: "ro_post_pro",
        type: "post",
        post: {
          author: { username: "logicfirst_", displayName: "Lennox H." },
          content: "The opposition's arguments on {{topic}} rely on outdated data and methodological shortcuts. Industry-funded think tanks consistently overstate risks to protect commercial interests. A closer look at their funding sources is instructive.",
          timestamp: "3h",
          likes: 14,
          comments: 6,
          reposts: 2,
        },
      },
      {
        stimulusId: "ro_post_con",
        type: "post",
        post: {
          author: { username: "followthemoney", displayName: "Cameron Q." },
          content: "Supporters of {{topic}} conveniently ignore that their lead advocates have career incentives to push this. The 'evidence' they cite comes from researchers whose grants depend on positive findings. This isn't consensus \u2014 it's institutional capture.",
          timestamp: "2h",
          likes: 11,
          comments: 8,
          reposts: 1,
        },
      },
      {
        stimulusId: "ro_pair_pro",
        type: "post_comment",
        post: {
          author: { username: "opendialogue", displayName: "Jules F." },
          content: "There are reasonable people on both sides of the {{topic}} debate. I think we need to listen more carefully to each other.",
          timestamp: "4h",
          likes: 8,
          comments: 4,
          reposts: 0,
        },
        comment: {
          author: { username: "accountability_", displayName: "Marlowe T." },
          content: "\"Reasonable people on both sides\" \u2014 respectfully, the opposition's track record on {{topic}} tells a different story. Their spokespeople have been consistently wrong on similar issues, and their arguments serve specific interests, not public welfare.",
          timestamp: "3h",
          likes: 7,
        },
      },
      {
        stimulusId: "ro_pair_con",
        type: "post_comment",
        post: {
          author: { username: "datadriven_x", displayName: "Sasha P." },
          content: "The expert consensus on {{topic}} clearly favors this direction. The evidence base is strong and growing.",
          timestamp: "5h",
          likes: 10,
          comments: 5,
          reposts: 1,
        },
        comment: {
          author: { username: "deeperanalysis", displayName: "River N." },
          content: "\"Expert consensus\" on {{topic}} that conveniently serves institutional agendas. Several credentialed critics have been marginalized for questioning the methodology. The proponents' selective citation practices don't withstand scrutiny.",
          timestamp: "4h",
          likes: 6,
        },
      },
    ],
  },
};

/**
 * Look up the stimulus set for a given condition.
 *
 * @param {string} communicationStyle - "emotional" or "rational"
 * @param {string} groupFocus - "neutral", "ingroup", or "outgroup"
 * @returns {Object|null} The stimulus set, or null if not found
 */
function getStimulusSet(communicationStyle, groupFocus) {
  const key = `${communicationStyle}_${groupFocus}`;
  return STIMULUS_EXEMPLARS[key] || null;
}

module.exports = { STIMULUS_EXEMPLARS, getStimulusSet };
